import React, { useState } from 'react';
import {
    Box,
    Button,
    Stack,
    Typography,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from '@mui/material';
import {
    AttachFile as AttachFileIcon,
    CloudUpload as CloudUploadIcon,
} from '@mui/icons-material';

function FileUpload({
    accept = '.pdf,.doc,.docx,.jpg,.jpeg,.png,.txt',
    categories = [],
    showCategory = false,
    showDescription = false,
    onFileSelect,
    onRemove,
    selectedFile: externalSelectedFile,
    fileBase64: externalFileBase64,
    category: externalCategory,
    description: externalDescription,
    onCategoryChange,
    onDescriptionChange,
    inputId = 'file-upload-input',
    disabled = false,
    required = false,
    error = false,
    helperText = '',
}) {
    const [internalSelectedFile, setInternalSelectedFile] = useState(null);
    const [internalFileBase64, setInternalFileBase64] = useState(null);
    const [internalCategory, setInternalCategory] = useState('');
    const [internalDescription, setInternalDescription] = useState('');

    const selectedFile = externalSelectedFile !== undefined ? externalSelectedFile : internalSelectedFile;
    const fileBase64 = externalFileBase64 !== undefined ? externalFileBase64 : internalFileBase64;
    const category = externalCategory !== undefined ? externalCategory : internalCategory;
    const description = externalDescription !== undefined ? externalDescription : internalDescription;

    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                const base64 = e.target.result.split(',')[1];
                
                if (externalSelectedFile === undefined) {
                    setInternalSelectedFile(file);
                    setInternalFileBase64(base64);
                }
                
                if (onFileSelect) {
                    onFileSelect({
                        file,
                        fileBase64: base64,
                        fileName: file.name,
                        fileSize: file.size,
                        category: category || '',
                        description: description || '',
                    });
                }
            };
            reader.readAsDataURL(file);
        }
    };

    const handleRemove = (e) => {
        e.stopPropagation();
        
        if (externalSelectedFile === undefined) {
            setInternalSelectedFile(null);
            setInternalFileBase64(null);
        }
        
        if (externalCategory === undefined) {
            setInternalCategory('');
        }
        if (externalDescription === undefined) {
            setInternalDescription('');
        }
        
        if (onRemove) {
            onRemove();
        }
    };

    const handleCategoryChange = (value) => {
        if (externalCategory === undefined) {
            setInternalCategory(value);
        }
        
        if (onCategoryChange) {
            onCategoryChange(value);
        }
        
        if (onFileSelect && selectedFile) {
            onFileSelect({
                file: selectedFile,
                fileBase64,
                fileName: selectedFile.name,
                fileSize: selectedFile.size,
                category: value,
                description,
            });
        }
    };

    const handleDescriptionChange = (value) => {
        if (externalDescription === undefined) {
            setInternalDescription(value);
        }
        
        if (onDescriptionChange) {
            onDescriptionChange(value);
        }
        
        if (onFileSelect && selectedFile) {
            onFileSelect({
                file: selectedFile,
                fileBase64,
                fileName: selectedFile.name,
                fileSize: selectedFile.size,
                category,
                description: value,
            });
        }
    };

    return (
        <Stack spacing={2}>
            <Box
                sx={{
                    border: error ? 'dashed 2px #d32f2f' : 'dashed 2px #ccc',
                    borderRadius: 2,
                    p: 3,
                    textAlign: 'center',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    opacity: disabled ? 0.6 : 1,
                    '&:hover': {
                        backgroundColor: disabled ? 'transparent' : '#f5f5f5',
                    }
                }}
                onClick={() => {
                    if (!disabled) {
                        document.getElementById(inputId).click();
                    }
                }}
            >
                <input
                    id={inputId}
                    type="file"
                    accept={accept}
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                    disabled={disabled}
                />
                {selectedFile ? (
                    <Stack spacing={1} alignItems="center">
                        <AttachFileIcon sx={{ fontSize: 48, color: 'primary.main' }} />
                        <Typography variant="body2">{selectedFile.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                            {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                        </Typography>
                        {onRemove && (
                            <Button 
                                size="small" 
                                onClick={handleRemove} 
                                color="error"
                                disabled={disabled}
                            >
                                Remover
                            </Button>
                        )}
                    </Stack>
                ) : (
                    <Stack spacing={1} alignItems="center">
                        <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                            Clique para selecionar um arquivo
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {accept}
                        </Typography>
                    </Stack>
                )}
            </Box>

            {error && helperText && (
                <Typography variant="caption" color="error" sx={{ mt: -1 }}>
                    {helperText}
                </Typography>
            )}

            {showCategory && categories.length > 0 && (
                <FormControl fullWidth>
                    <InputLabel>Categoria</InputLabel>
                    <Select
                        value={category}
                        onChange={(e) => handleCategoryChange(e.target.value)}
                        label="Categoria"
                        disabled={disabled}
                    >
                        {categories.map((cat) => (
                            <MenuItem key={cat.value} value={cat.value}>
                                {cat.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )}

            {showDescription && (
                <TextField
                    label="Descrição"
                    value={description}
                    onChange={(e) => handleDescriptionChange(e.target.value)}
                    fullWidth
                    multiline
                    rows={3}
                    disabled={disabled}
                />
            )}
        </Stack>
    );
}

export default FileUpload;

